<template>
  <div class="space-y-6">
    <!-- Configuração específica para Fluxo de Vendas (ID 2) -->
    <template v-if="flowType === 2">
      <!-- Toggle para ativar/desativar carrinho -->
      <div class="flex items-center justify-between p-4 bg-n-slate-1 dark:bg-n-slate-3 rounded-lg border border-n-slate-4">
        <div class="flex-1">
          <h3 class="text-base font-medium text-n-slate-12 mb-1">
            Carrinho de Compras
          </h3>
          <p class="text-sm text-n-slate-11">
            Ative para permitir que o agente adicione produtos ao carrinho durante a conversa
          </p>
        </div>
        <label class="relative inline-flex items-center cursor-pointer ml-4">
          <input
            type="checkbox"
            v-model="config.shoppingCartEnabled"
            class="sr-only peer"
            @change="updateConfig"
          >
          <div class="w-11 h-6 bg-n-slate-5 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-n-slate-7 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-n-slate-6 peer-checked:bg-blue-600"></div>
        </label>
      </div>
    </template>

    <!-- Configuração específica para Fluxo de Qualificação (ID 1) -->
    <template v-else-if="flowType === 1">
      <!-- Placeholder para futuras configurações de qualificação -->
      <div class="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        <p class="text-sm text-amber-800 dark:text-amber-200">
          Configurações específicas para o fluxo de Qualificação serão adicionadas em breve.
        </p>
      </div>
    </template>

    <!-- Configuração específica para Fluxo de Agendamento (ID 3) -->
    <template v-else-if="flowType === 3">
      <!-- Aviso para configurar agenda e profissionais -->
      <div class="p-6 bg-n-slate-1 dark:bg-n-slate-3 rounded-lg border border-n-slate-4">
        <div class="flex items-start gap-4">
          <!-- Ícone de informação -->
          <div class="flex-shrink-0 mt-0.5">
            <span class="i-lucide-info text-n-brand text-xl" />
          </div>

          <div class="flex-1">
            <p class="text-sm text-n-slate-11 mb-4">
              Para que o agente de agendamento funcione corretamente, certifique-se de vincular a <strong>agenda</strong> da empresa e cadastrar os <strong>profissionais</strong> e seus <strong>horários disponíveis</strong>.
            </p>

            <!-- Botões de ação -->
            <div class="flex flex-col sm:flex-row gap-3">
              <button
                @click="openCalendarPage"
                type="button"
                class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand-dark rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-brand"
              >
                <span class="i-lucide-calendar mr-2" />
                Configurar Agenda
              </button>

              <button
                @click="openProfessionalsPage"
                type="button"
                class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-slate-1 hover:bg-n-slate-2 dark:bg-n-slate-4 dark:hover:bg-n-slate-5 border border-n-slate-6 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-brand"
              >
                <span class="i-lucide-users mr-2" />
                Cadastrar Profissionais
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Fallback para outros tipos de fluxo -->
    <template v-else>
      <div class="p-4 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg">
        <p class="text-sm text-gray-800 dark:text-gray-200">
          Selecione um fluxo de conversa para ver as configurações específicas disponíveis.
        </p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { reactive, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
  flowType: {
    type: Number,
    default: null
  },
  initialConfig: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update', 'valid']);

const route = useRoute();

// Obter o accountId da rota
const currentAccountId = route.params.accountId;

// Funções para abrir páginas em nova guia
const openCalendarPage = () => {
  const url = `/app/accounts/${currentAccountId}/appointments/calendar`;
  window.open(url, '_blank');
};

const openProfessionalsPage = () => {
  const url = `/app/accounts/${currentAccountId}/appointments/professionals`;
  window.open(url, '_blank');
};

// Configuração padrão para cada tipo de fluxo
const getDefaultConfig = (flowType) => {
  switch(flowType) {
    case 2: // Vendas
      return {
        shoppingCartEnabled: false
      };
    default:
      return {};
  }
};

// Estado local reativo
const config = reactive(
  props.initialConfig && Object.keys(props.initialConfig).length > 0
    ? { ...props.initialConfig }
    : getDefaultConfig(props.flowType)
);

// Função para atualizar configuração
const updateConfig = () => {
  emit('update', config);
  emit('valid', true); // Sempre válido pois é opcional
};

// Atualizar configuração quando o tipo de fluxo mudar
watch(() => props.flowType, (newFlowType) => {
  // Se mudou para um tipo diferente, reinicializar com configuração padrão
  const newConfig = getDefaultConfig(newFlowType);
  Object.keys(config).forEach(key => {
    delete config[key];
  });
  Object.keys(newConfig).forEach(key => {
    config[key] = newConfig[key];
  });
  updateConfig();
});

// Validar sempre que houver mudanças na configuração
watch(config, () => {
  updateConfig();
}, { deep: true });

// Emitir validação inicial
onMounted(() => {
  emit('valid', true);
  updateConfig();
});
</script>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>